import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import { createAction } from '@reduxjs/toolkit';
import {
  DeleteRedactionCustomStatusRequestPayload,
  FetchRedactionStatusesResponse,
  ReorderRedactionCustomStatusesRequestPayload,
  SaveRedactionCustomStatusRequestPayload,
  SaveRedactionStatusesRequestPayload,
  SaveRedactionStatusesSuccessPayload,
  SetEditCustomStatusPayload,
} from '../models';

export const FETCH_REDACTION_STATUSES = createAction(
  'REDACTION_STATUSES/FETCH_REDACTION_STATUSES'
);

export const FETCH_REDACTION_STATUSES_SUCCESS =
  createGraphQLSuccessAction<FetchRedactionStatusesResponse>(
    'REDACTION_STATUSES/FETCH_REDACTION_STATUSES_SUCCESS'
  );

export const FETCH_REDACTION_STATUSES_FAILURE = createGraphQLFailureAction(
  'REDACTION_STATUSES/FETCH_REDACTION_STATUSES_FAILURE'
);

export const SAVE_REDACTION_STATUSES_REQUEST =
  createAction<SaveRedactionStatusesRequestPayload>(
    'REDACTION_STATUSES/SAVE_REDACTION_STATUSES_REQUEST'
  );

export const SAVE_REDACTION_STATUSES_SUCCESS =
  createAction<SaveRedactionStatusesSuccessPayload>(
    'REDACTION_STATUSES/SAVE_REDACTION_STATUSES_SUCCESS'
  );

export const SAVE_REDACTION_STATUSES_FAILURE = createAction(
  'REDACTION_STATUSES/SAVE_REDACTION_STATUSES_FAILURE'
);

// ---------------------------------------------------------------------------
// Custom statuses (VE-24546)
// ---------------------------------------------------------------------------

export const SAVE_REDACTION_CUSTOM_STATUS_REQUEST =
  createAction<SaveRedactionCustomStatusRequestPayload>(
    'REDACTION_STATUSES/SAVE_REDACTION_CUSTOM_STATUS_REQUEST'
  );
export const SAVE_REDACTION_CUSTOM_STATUS_SUCCESS = createAction<{
  readonly statusLabel: string;
  /** Echoed from the request so only the drawer's own save closes it. */
  readonly closeDrawer?: boolean;
}>('REDACTION_STATUSES/SAVE_REDACTION_CUSTOM_STATUS_SUCCESS');
export const SAVE_REDACTION_CUSTOM_STATUS_FAILURE = createAction(
  'REDACTION_STATUSES/SAVE_REDACTION_CUSTOM_STATUS_FAILURE'
);

export const DELETE_REDACTION_CUSTOM_STATUS_REQUEST =
  createAction<DeleteRedactionCustomStatusRequestPayload>(
    'REDACTION_STATUSES/DELETE_REDACTION_CUSTOM_STATUS_REQUEST'
  );
export const DELETE_REDACTION_CUSTOM_STATUS_SUCCESS = createAction<{
  readonly statusLabel: string;
}>('REDACTION_STATUSES/DELETE_REDACTION_CUSTOM_STATUS_SUCCESS');
export const DELETE_REDACTION_CUSTOM_STATUS_FAILURE = createAction(
  'REDACTION_STATUSES/DELETE_REDACTION_CUSTOM_STATUS_FAILURE'
);

/**
 * Restores a soft-deleted custom status (VE-24546 follow-up): clears
 * isDeleted and places the row last on the statuses page.
 */
export const RESTORE_REDACTION_CUSTOM_STATUS_REQUEST =
  createAction<DeleteRedactionCustomStatusRequestPayload>(
    'REDACTION_STATUSES/RESTORE_REDACTION_CUSTOM_STATUS_REQUEST'
  );
export const RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS = createAction<{
  readonly statusLabel: string;
}>('REDACTION_STATUSES/RESTORE_REDACTION_CUSTOM_STATUS_SUCCESS');
export const RESTORE_REDACTION_CUSTOM_STATUS_FAILURE = createAction(
  'REDACTION_STATUSES/RESTORE_REDACTION_CUSTOM_STATUS_FAILURE'
);

/**
 * Removes a soft-deleted custom status's SDO for good (VE-24546 follow-up).
 * Reuses the DELETE success/failure actions, so the toast and refetch flow
 * are shared with soft delete.
 */
export const PERMANENTLY_DELETE_REDACTION_CUSTOM_STATUS_REQUEST =
  createAction<DeleteRedactionCustomStatusRequestPayload>(
    'REDACTION_STATUSES/PERMANENTLY_DELETE_REDACTION_CUSTOM_STATUS_REQUEST'
  );

export const REORDER_REDACTION_CUSTOM_STATUSES_REQUEST =
  createAction<ReorderRedactionCustomStatusesRequestPayload>(
    'REDACTION_STATUSES/REORDER_REDACTION_CUSTOM_STATUSES_REQUEST'
  );
export const REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS = createAction(
  'REDACTION_STATUSES/REORDER_REDACTION_CUSTOM_STATUSES_SUCCESS'
);
export const REORDER_REDACTION_CUSTOM_STATUSES_FAILURE = createAction(
  'REDACTION_STATUSES/REORDER_REDACTION_CUSTOM_STATUSES_FAILURE'
);

export const TOGGLE_STATUS_DRAWER = createAction(
  'REDACTION_STATUSES/TOGGLE_STATUS_DRAWER'
);
export const SET_EDIT_CUSTOM_STATUS = createAction<SetEditCustomStatusPayload>(
  'REDACTION_STATUSES/SET_EDIT_CUSTOM_STATUS'
);
export const CLEAR_EDIT_CUSTOM_STATUS = createAction(
  'REDACTION_STATUSES/CLEAR_EDIT_CUSTOM_STATUS'
);

export const actionFetchRedactionStatuses = () => FETCH_REDACTION_STATUSES();

export const actionSaveRedactionStatuses = (
  payload: SaveRedactionStatusesRequestPayload
) => SAVE_REDACTION_STATUSES_REQUEST(payload);

export const actionSaveRedactionCustomStatus = (
  payload: SaveRedactionCustomStatusRequestPayload
) => SAVE_REDACTION_CUSTOM_STATUS_REQUEST(payload);

export const actionDeleteRedactionCustomStatus = (
  payload: DeleteRedactionCustomStatusRequestPayload
) => DELETE_REDACTION_CUSTOM_STATUS_REQUEST(payload);

export const actionRestoreRedactionCustomStatus = (
  payload: DeleteRedactionCustomStatusRequestPayload
) => RESTORE_REDACTION_CUSTOM_STATUS_REQUEST(payload);

export const actionPermanentlyDeleteRedactionCustomStatus = (
  payload: DeleteRedactionCustomStatusRequestPayload
) => PERMANENTLY_DELETE_REDACTION_CUSTOM_STATUS_REQUEST(payload);

export const actionReorderRedactionCustomStatuses = (
  payload: ReorderRedactionCustomStatusesRequestPayload
) => REORDER_REDACTION_CUSTOM_STATUSES_REQUEST(payload);

export const actionToggleStatusDrawer = () => TOGGLE_STATUS_DRAWER();

export const actionSetEditCustomStatus = (
  payload: SetEditCustomStatusPayload
) => SET_EDIT_CUSTOM_STATUS(payload);

export const actionClearEditCustomStatus = () => CLEAR_EDIT_CUSTOM_STATUS();
